import React from 'react';
import { Team } from '../../types/organization.types';
import { LedgerSkeleton } from '../shell/LedgerSkeleton';
import { cn } from '../../lib/cn';

// Display math only ("N more entries", "Page X / Y"). The server default page
// size is 20 but DB-configurable — if that setting changes, this display drifts.
// Prev/Next bounds do NOT drift: they come from the DRF envelope via hasNext/hasPrevious.
const PAGE_SIZE = 20;

export interface TeamsTableProps {
  rows: Team[];
  count: number;
  page: number;
  isPending: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onPageChange: (page: number) => void;
  onResetView: () => void;
}

const activeSquareClass = (isActive: boolean) => cn('h-2.5 w-2.5 border border-ink', isActive && 'bg-ink');

const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

export const TeamsTable: React.FC<TeamsTableProps> = (props) => {
  const { rows, count, page, isPending, hasNext, hasPrevious, selectedId } = props;

  if (isPending) return <LedgerSkeleton lines={8} className="mt-4" />;

  if (rows.length === 0) {
    return (
      <p className="py-6 font-serif text-scale-1 italic text-ink-muted">
        Nothing on file for this view.{' '}
        <button type="button" onClick={props.onResetView} className="cursor-pointer underline hover:text-accent">
          Show all teams
        </button>
      </p>
    );
  }

  const pageCount = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const remaining = Math.max(0, count - page * PAGE_SIZE);

  return (
    <div>
      <table className="hidden w-full border-collapse md:table" aria-label="Teams">
        <thead>
          <tr className="border-b border-ink">
            <th className="cap-label py-1 text-left font-normal">Name</th>
            <th className="cap-label py-1 text-left font-normal">Company</th>
            <th className="cap-label py-1 text-left font-normal">Departments</th>
            <th className="cap-label py-1 text-left font-normal">Members</th>
            <th className="cap-label py-1 text-left font-normal">Active</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((team) => (
            <tr
              key={team.id}
              data-testid="team-row"
              onClick={() => props.onSelect(team.id)}
              aria-current={team.id === selectedId ? 'true' : undefined}
              className={cn(
                'cursor-pointer border-b border-rule-faint text-scale-1 hover:bg-accent-soft',
                team.id === selectedId && 'bg-accent-soft shadow-[inset_2px_0_0_var(--color-accent)]',
              )}
            >
              <td className="py-1.5 pr-3">
                <span className="block">
                  <span className="font-medium text-ink">{team.name}</span>
                  {team.description && (
                    <span className="block max-w-[28ch] truncate text-scale-0 text-ink-muted">{team.description}</span>
                  )}
                </span>
              </td>
              <td className="py-1.5 pr-3">
                <span className="text-ink">{team.companyName || '—'}</span>
              </td>
              <td className="py-1.5 pr-3">
                <span className="font-mono text-ink-muted">{team.departmentsCount}</span>
              </td>
              <td className="py-1.5 pr-3">
                <span className="font-mono text-ink-muted">{team.usersCount}</span>
              </td>
              <td className="py-1.5 pr-3">
                <span aria-hidden className={cn('inline-block', activeSquareClass(team.isActive))} />
                <span className="sr-only">{team.isActive ? 'Active' : 'Inactive'}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile: rows reflow to two-line entries */}
      <div className="md:hidden">
        {rows.map((team) => (
          <button
            key={team.id}
            type="button"
            onClick={() => props.onSelect(team.id)}
            className="block w-full border-b border-rule-faint py-2.5 text-left"
          >
            <span className="flex items-center text-scale-1 font-semibold text-ink">
              <span aria-hidden className={cn('mr-1.5 shrink-0', activeSquareClass(team.isActive))} />
              <span className="sr-only">{team.isActive ? 'Active' : 'Inactive'}</span>
              {team.name}
            </span>
            <span className="mt-0.5 block font-mono text-scale-0 text-ink-muted">
              {team.companyName || '—'} · {pluralize(team.usersCount, 'member')}
            </span>
          </button>
        ))}
      </div>

      <div className="mt-2 flex items-baseline justify-between">
        <span className="font-serif text-scale-1 italic text-ink-muted">
          {remaining > 0 ? `${remaining} more entries — j/k to move, Enter to peek` : 'j/k to move, Enter to peek'}
        </span>
        <span className="flex items-baseline gap-3">
          <button type="button" disabled={!hasPrevious} onClick={() => props.onPageChange(page - 1)}
            className="cap-label cursor-pointer disabled:cursor-default disabled:opacity-40 hover:!text-ink">
            ← Prev
          </button>
          <span className="cap-label">Page {page} / {pageCount}</span>
          <button type="button" disabled={!hasNext} onClick={() => props.onPageChange(page + 1)}
            className="cap-label cursor-pointer disabled:cursor-default disabled:opacity-40 hover:!text-ink">
            Next →
          </button>
        </span>
      </div>
    </div>
  );
};
